import { isSameEthereumAddress } from '@swarm/core/web3'
import { AbstractToken } from '@swarm/types/tokens'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon, Text } from 'rimble-ui'
import styled from 'styled-components/macro'
import { FlexBasisProps, FlexboxProps, MarginProps } from 'styled-system'

import Clickable from '@ui/presentational/Clickable'

import TokenIcon from './TokenIcon'
import TokenSelectorModal from './TokenSelector/TokenSelectorModal'

const StyledButton = styled(Button.Text)`
  outline: none;
  height: 32px;
  /* 
    Button.Text sets a z-index on the label so we need to reset its stacking context
    so it doesn't overlap with other items in the page like the header
  */
  transform: translate(0);
`

const StyledClickable = styled(Clickable)`
  border-radius: 4px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  border: 1px solid #e9eaf2;
  height: 32px;

  &:hover {
    background: rgba(200, 200, 200, 0.2);
  }
`

const PillContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
`

export const getAssetFilter = <T extends AbstractToken = AbstractToken>(
  tokens: T[],
  assets: string[],
) =>
  tokens.filter((token) => {
    return assets.some((asset) => isSameEthereumAddress(asset, token.id))
  })

interface TokenPillProps extends MarginProps {
  token: AbstractToken
  onRemove: () => void
}

const TokenPill = ({ token, onRemove, ...props }: TokenPillProps) => {
  return (
    <StyledClickable alignItems="center" onClick={onRemove} {...props}>
      <TokenIcon
        symbol={token.symbol}
        name={token.name}
        width="16px"
        height="16px"
        mr={1}
      />
      <Text color="black" fontSize="14px" fontWeight={600} mr={1}>
        {token.symbol}
      </Text>
      <Icon name="Close" size="16px" color="black" />
    </StyledClickable>
  )
}

interface AssetFilterProps extends FlexBasisProps, FlexboxProps {
  tokens: AbstractToken[]
  loading?: boolean
  category?: string
  setCategory?: (category: string | undefined) => void
  value: AbstractToken[]
  onSubmit: (value: AbstractToken[]) => void
  showTokenInfo?: boolean
}

const AssetFilter = ({
  tokens,
  loading,
  category,
  setCategory,
  value,
  onSubmit,
  showTokenInfo = false,
  ...props
}: AssetFilterProps) => {
  const { t } = useTranslation('common')
  const [tempSelection, setTempSelection] = useState(value)
  const [modalOpen, setModalOpen] = useState(false)
  const [tempCategory, setTempCategory] = useState<string | undefined>(category)

  const toggleCategory = (newCategory: string): void => {
    setTempCategory((currentTempCategory) => {
      if (currentTempCategory === newCategory) {
        return undefined
      }
      return newCategory
    })
  }

  const handleFilterButtonClick = () => {
    if (value.length) {
      onSubmit([])
    } else {
      setTempSelection(value)
      setModalOpen(true)
    }
  }
  const handleModalClose = () => setModalOpen(false)

  const handleTokenSelection = (token: AbstractToken) => {
    if (tempSelection.some((selectedToken) => token.id === selectedToken.id)) {
      setTempSelection(
        tempSelection.filter((selectedToken) => token.id !== selectedToken.id),
      )
    } else {
      setTempSelection([...tempSelection, token])
    }
  }

  const handleSubmit = () => {
    setModalOpen(false)
    onSubmit(tempSelection)
    setCategory?.(tempCategory)
  }

  const handleAssetRemove = (asset: AbstractToken) => () => {
    onSubmit(value.filter((token) => token.id !== asset.id))
  }

  return (
    <Box {...props}>
      <PillContainer>
        {value.map((token) => (
          <TokenPill
            key={token.id}
            token={token}
            onRemove={handleAssetRemove(token)}
          />
        ))}
        <StyledButton
          size="medium"
          onClick={handleFilterButtonClick}
          flexShrink={0}
          ml={0}
          p={0}
        >
          {value.length ? (
            t('filterAssets.clearAll')
          ) : (
            <>
              <Icon name="FilterList" size="16px" mr={1} />
              {t('filterAssets.title')}
            </>
          )}
        </StyledButton>
      </PillContainer>
      {modalOpen && (
        <TokenSelectorModal
          isOpen={modalOpen}
          isFilter
          onClose={handleModalClose}
          selected={tempSelection}
          onSelection={handleTokenSelection}
          onCategorySelection={toggleCategory}
          onButtonClick={handleSubmit}
          title={t('filterAssets.title')}
          buttonLabel={t('filterAssets.confirmFilter')}
          multiple
          tokens={tokens}
          loading={loading}
          selectedCategory={tempCategory}
          showTokenInfo={showTokenInfo}
        />
      )}
    </Box>
  )
}

export default AssetFilter
